// @ts-nocheck
import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { notifyRosterSubmitted } from '@/lib/eventNotifications';

interface RosterSubmissionResult {
  success: boolean;
  message: string;
  submittedAt?: string;
}

export const useRosterSubmission = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submitRoster = async (
    eventId: string,
    teamId: string,
    teamName: string,
    eventName: string,
    lockRoster: boolean = false
  ): Promise<RosterSubmissionResult> => {
    setIsSubmitting(true);
    
    try {
      // First, verify the team has players selected for THIS EVENT
      // Check event_team_rosters (event-specific selections), not coach_team_rosters (master roster)
      const { count: playerCount, error: countError } = await supabase
        .from('event_team_rosters')
        .select('*', { count: 'exact', head: true })
        .eq('event_id', eventId)
        .eq('team_id', teamId);

      if (countError) throw countError;
      
      if (playerCount === 0 || playerCount === null) {
        toast.error('Cannot submit an empty roster. Please select players for this event first.');
        return {
          success: false,
          message: 'No players selected for this event'
        };
      }

      // Find the event registration for this event and team
      // We need to find the registration through coach_invitations
      const { data: invitation, error: invError } = await supabase
        .from('coach_invitations')
        .select('registration_id')
        .eq('event_id', eventId)
        .eq('team_id', teamId)
        .single();

      if (invError && invError.code !== 'PGRST116') {
        console.error('Error finding registration:', invError);
      }

      const registrationId = invitation?.registration_id;
      const submittedAt = new Date().toISOString();

      if (registrationId) {
        // Update the registration with roster submission info
        const { error: updateError } = await supabase
          .from('registrations')
          .update({
            roster_submitted_at: submittedAt,
            roster_locked: lockRoster
          })
          .eq('id', registrationId);

        if (updateError) {
          console.error('Error updating registration:', updateError);
          throw updateError;
        }
      } else {
        console.log('No registration found for this team/event combination, but continuing with notification');
      }

      // Send notification to admin about roster submission
      try {
        await notifyRosterSubmitted(eventId, eventName, teamName);
        console.log('✅ Roster submission notification sent');
      } catch (notifyError) {
        console.error('Failed to send notification:', notifyError);
        // Don't fail the submission if notification fails
      }

      toast.success(`Roster submitted successfully! ${playerCount} players registered.`);
      
      return {
        success: true,
        message: `Roster submitted with ${playerCount} players`,
        submittedAt
      };

    } catch (error) {
      console.error('Error submitting roster:', error);
      toast.error('Failed to submit roster. Please try again.');
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error'
      };
    } finally {
      setIsSubmitting(false);
    }
  };

  const getRosterSubmissionStatus = async (
    eventId: string,
    teamId: string
  ): Promise<{ isSubmitted: boolean; submittedAt?: string; isLocked: boolean }> => {
    try {
      // First get the registration_id from coach_invitations
      const { data: invitation, error: invError } = await supabase
        .from('coach_invitations')
        .select('registration_id')
        .eq('event_id', eventId)
        .eq('team_id', teamId)
        .single();

      if (invError || !invitation?.registration_id) {
        // No invitation or registration found
        return { isSubmitted: false, isLocked: false };
      }

      // Then query registrations separately
      const { data: registration, error: regError } = await supabase
        .from('registrations')
        .select('roster_submitted_at, roster_locked')
        .eq('id', invitation.registration_id)
        .single();

      if (regError) {
        console.log('No registration found:', regError.message);
        return { isSubmitted: false, isLocked: false };
      }
      
      return {
        isSubmitted: !!registration?.roster_submitted_at,
        submittedAt: registration?.roster_submitted_at,
        isLocked: registration?.roster_locked || false
      };
    } catch (error) {
      console.error('Error getting submission status:', error);
      return { isSubmitted: false, isLocked: false };
    }
  };

  return {
    isSubmitting,
    submitRoster,
    getRosterSubmissionStatus
  };
};
